/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { FileCode2, Globe, Layers, Puzzle, Receipt, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api } from '../../api';
import type { AddonMode, AddonService, CostComponent, LifecycleService } from '../../api/areas/services';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { QuickLinks } from '../../components/flow';
import { hubUrl } from '../../nav/hubs';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { Loading } from '../../components/States';
import { formatCount, formatDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { Amount } from '../commerce/shared';
import { useEnum, useLocalName } from './shared';

/** Mandatory / default-on / optional, as a small badge. */
export function ModeBadge({ mode }: { mode: AddonMode | null }) {
  const e = useEnum();
  if (!mode) return <span className="text-xs text-muted-foreground">{e('mode', 'OFF')}</span>;
  return <Badge tone={mode === 'MANDATORY' ? 'warn' : mode === 'DEFAULT' ? 'info' : 'neutral'}>{e('mode', mode)}</Badge>;
}

/**
 * `/products?tab=services`: "Dịch vụ tên miền" — the add-on catalogue of `config/addons.yaml`
 * (read-only), which TLDs offer each one and how, their prices per TLD, and the lifecycle
 * services with the add-ons that can come with them. Enabling an add-on on a TLD and its price
 * are set on the TLD's page.
 */
export function DomainServicesPage() {
  const { t } = useTranslation('services');
  const { t: tu } = useTranslation('ux');
  const e = useEnum();
  const name = useLocalName();
  useDocumentTitle(t('catalogue.title'));
  const result = useApi((signal) => api.listAddonCatalogue({ signal }), []);
  const comps = useApi((signal) => api.listCostComponents({}, { signal }), []);
  const reload = () => {
    result.reload();
    comps.reload();
  };

  const lifecycleCols: Column<LifecycleService>[] = [
    { key: 'op', header: t('catalogue.col.operation'), cell: (l) => <span className="font-medium">{e('operation', l.operation)}</span> },
    { key: 'rules', header: t('catalogue.col.rules'), numeric: true, cell: (l) => formatCount(l.price_rules) },
    {
      key: 'addons',
      header: t('catalogue.col.addons'),
      cell: (l) =>
        l.addons.length ? (
          <ul className="grid gap-0.5 text-xs">
            {l.addons.map((code) => {
              const a = result.status === 'success' ? result.data.addons.find((x) => x.code === code) : undefined;
              return (
                <li key={code} title={code}>
                  {a ? name(a) : code}
                </li>
              );
            })}
          </ul>
        ) : (
          t('common.none')
        ),
    },
  ];

  const addonCols: Column<AddonService>[] = [
    {
      key: 'name',
      header: t('catalogue.col.name'),
      cell: (a) => (
        <div className="grid gap-0.5">
          <span className="inline-flex flex-wrap items-center gap-2 font-medium">
            {name(a)}
            {!a.active ? <Badge tone="neutral">{t('catalogue.inactive')}</Badge> : null}
            {a.free ? <Badge tone="ok">{t('catalogue.free')}</Badge> : null}
          </span>
          <span className="font-mono text-xs text-muted-foreground">{a.code}</span>
        </div>
      ),
    },
    { key: 'billing', header: t('catalogue.col.billing'), cell: (a) => <span title={t(`catalogue.billingHint.${a.billing_model}`)}>{e('billing', a.billing_model)}</span> },
    { key: 'ops', header: t('catalogue.col.operations'), cell: (a) => <span className="text-xs">{a.operations.map((o) => e('operation', o)).join(', ')}</span> },
    { key: 'tax', header: t('catalogue.col.tax'), cell: (a) => (a.tax_category === 'VAT' && a.tax_code ? `VAT ${a.tax_code}` : e('tax', a.tax_category)) },
    {
      key: 'hints',
      header: t('catalogue.col.hints'),
      cell: (a) =>
        a.default_tlds.length ? (
          <ul className="grid gap-0.5 text-xs">
            {a.default_tlds.map((h) => (
              <li key={h.class}>
                {e('tldClass', h.class)}: {e('mode', h.mode)}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-muted-foreground">{t('common.none')}</span>
        ),
    },
    {
      key: 'tlds',
      header: t('catalogue.col.tlds'),
      cell: (a) =>
        a.tlds.length ? (
          <ul className="flex max-w-80 flex-wrap gap-1">
            {a.tlds.map((x) => (
              <li key={x.tld_id}>
                <Link to={`/registry/tlds/${encodeURIComponent(x.tld_id)}?tab=pricing`} className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs hover:bg-muted" title={e('mode', x.mode)}>
                  .{x.zone}
                  {x.mode === 'MANDATORY' ? <span className="text-warning">*</span> : null}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-muted-foreground">{t('catalogue.noTld')}</span>
        ),
    },
    {
      key: 'prices',
      header: t('catalogue.col.prices'),
      cell: (a) =>
        a.registry_costs.length + a.prices.length === 0 ? (
          <span className="text-xs text-muted-foreground">{a.free ? t('catalogue.free') : t('catalogue.noPrice')}</span>
        ) : (
          <ul className="grid gap-0.5 text-xs">
            {a.registry_costs.map((c) => (
              <li key={c.id}>
                .{c.tld}
                {c.for_operation ? ` (${e('operation', c.for_operation)})` : ''}: {c.amount_basis === 'PERCENT_OF_REGISTRY_COST' ? t('catalogue.percentOfCost', { value: c.amount }) : <Amount amount={c.amount} currency={c.currency} />}
              </li>
            ))}
            {a.prices.map((p) => (
              <li key={p.rule_id}>
                .{p.tld} · <span className="font-mono">{p.price_book}</span>: {p.rule_type === 'FIXED_PRICE' ? <Amount amount={p.value} currency={p.currency} /> : `${e('ruleType', p.rule_type)} ${p.value}`}
              </li>
            ))}
          </ul>
        ),
    },
    { key: 'domains', header: t('catalogue.col.domains'), numeric: true, cell: (a) => formatCount(a.domains_active) },
  ];

  const compCols: Column<CostComponent>[] = [
    { key: 'tld', header: t('catalogue.col2.tld'), cell: (c) => <span className="font-medium">.{c.tld}</span> },
    { key: 'op', header: t('catalogue.col2.operation'), cell: (c) => e('operation', c.operation) },
    { key: 'component', header: t('catalogue.col2.component'), cell: (c) => name({ name_vi: c.description_vi, name_en: c.description_en }) },
    { key: 'amount', header: t('catalogue.col2.amount'), numeric: true, cell: (c) => <Amount amount={c.amount} currency={c.currency} /> },
    { key: 'tax', header: t('catalogue.col2.tax'), cell: (c) => e('tax', c.tax_category) },
    { key: 'from', header: t('catalogue.col2.from'), className: 'whitespace-nowrap text-xs', cell: (c) => formatDate(c.effective_from) },
  ];

  return (
    <>
      <PageHeader
        title={t('catalogue.title')}
        crumbs={[{ label: t('crumbs.business') }]}
        subtitle={t('catalogue.subtitle')}
        actions={
          <Button type="button" variant="outline" size="sm" onClick={reload}>
            <RefreshCw /> {t('common.refresh')}
          </Button>
        }
      />
      {result.status === 'loading' ? <Loading rows={6} /> : null}
      {result.status === 'error' ? <DataError error={result.error} title={t('common.loadFailed')} onRetry={result.reload} /> : null}
      {result.status === 'success' ? (
        <div className="flex flex-col gap-6">
          <QuickLinks
            label={tu('catalogue.related')}
            links={[
              { label: tu('catalogue.linkQuote'), to: hubUrl('products', 'quote') },
              { label: tu('catalogue.linkPrices'), to: hubUrl('products', 'pricing') },
              { label: tu('catalogue.linkTlds'), to: hubUrl('products', 'tlds') },
            ]}
          />
          <StatGrid>
            <StatCard label={tu('catalogue.addons')} icon={Puzzle} value={result.data.addons.filter((a) => a.kind !== 'OPERATION_FEE').length} hint={tu('catalogue.addonsHint')} />
            <StatCard label={tu('catalogue.fees')} icon={Receipt} value={result.data.addons.filter((a) => a.kind === 'OPERATION_FEE' || a.tax_category === 'KCT').length} hint={tu('catalogue.feesHint')} />
            <StatCard label={tu('catalogue.tlds')} icon={Globe} value={new Set(result.data.addons.flatMap((a) => a.tlds.map((x) => x.tld_id))).size} hint={tu('catalogue.tldsHint')} />
            <StatCard label={tu('catalogue.inUse')} icon={Layers} value={formatCount(result.data.addons.reduce((n, a) => n + a.domains_active, 0))} hint={tu('catalogue.inUseHint', { version: result.data.catalog_version })} />
          </StatGrid>
          <Callout tone="info" title={t('catalogue.fileTitle')}>
            <p className="flex flex-wrap items-center gap-1.5">
              <FileCode2 className="size-4" />
              {t('catalogue.fileBody', { file: result.data.source, version: result.data.catalog_version })}
            </p>
          </Callout>
          <Section title={t('catalogue.addons')} description={t('catalogue.addonsHint')} flush>
            <DataTable columns={addonCols} rows={result.data.addons} rowKey={(a) => a.code} />
          </Section>
          <Section title={t('catalogue.lifecycle')} description={t('catalogue.lifecycleHint')} flush>
            <DataTable columns={lifecycleCols} rows={result.data.lifecycle} rowKey={(l) => l.operation} />
          </Section>
          {comps.status === 'success' && comps.data.items.length > 0 ? (
            <Section title={t('catalogue.components')} description={t('catalogue.componentsHint')} flush>
              <DataTable columns={compCols} rows={comps.data.items} rowKey={(c) => c.id} />
            </Section>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
